import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { ConfirmDeleteModal } from '../../components/common/ConfirmDeleteModal';
import { deleteAppUser, listExtranetAgencies, listExtranetUsers, type ExtranetUser } from '../../services/userAdministration';
import { fltSelClass } from '../../utils/alSelClass';

export default function ExtranetUsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<ExtranetUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [agencyOptions, setAgencyOptions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ExtranetUser | null>(null);

  const [qAgency, setQAgency] = useState('');
  const [qEmail, setQEmail] = useState('');
  const [status, setStatus] = useState<'' | 'Active' | 'Inactive'>('');

  const [applied, setApplied] = useState<{ agency: string; email: string; status: '' | 'Active' | 'Inactive' }>({
    agency: '',
    email: '',
    status: '',
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    void listExtranetUsers({ agencyName: applied.agency, email: applied.email, status: applied.status })
      .then((r) => {
        if (!alive) return;
        if (!r.success) throw new Error(r.message);
        setRows(r.data);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : t('admin.extranetUsers.loadFailed'));
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [applied.agency, applied.email, applied.status, t]);

  useEffect(() => {
    let alive = true;
    void listExtranetAgencies().then((r) => {
      if (!alive) return;
      if (r.success) setAgencyOptions(r.data.map((a) => a.name));
    });
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const a = applied.agency.trim().toLowerCase();
    const e = applied.email.trim().toLowerCase();
    return rows.filter((r) => {
      if (a && !r.agencyName.toLowerCase().includes(a)) return false;
      if (e && !r.email.toLowerCase().includes(e)) return false;
      if (applied.status && r.status !== applied.status) return false;
      return true;
    });
  }, [rows, applied.agency, applied.email, applied.status]);

  const runDelete = async (r: ExtranetUser) => {
    setDeletingId(r.id);
    setError(null);
    try {
      if (import.meta.env.DEV) console.log('[ExtranetUsersPage] delete confirmed', { id: r.id, name: r.contactPersonName || r.email });
      const res = await deleteAppUser(r.id);
      if (!res.success) throw new Error(res.message);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      navigate(location.pathname + location.search, {
        replace: true,
        state: { toast: { message: t('admin.extranetUsers.deleteSuccess'), variant: 'success' } },
      });
    } finally {
      setDeletingId(null);
    }
  };

  const columns = useMemo<Column<ExtranetUser>[]>(() => {
    return [
      {
        key: 'agency',
        header: t('admin.shared.agencyName'),
        getValue: (r) => r.agencyName,
        render: (r) => <span style={{ fontWeight: 600 }}>{r.agencyName}</span>,
      },
      { key: 'code', header: t('admin.shared.code'), getValue: (r) => r.companyCode ?? '' },
      { key: 'email', header: t('profile.emailUsername'), getValue: (r) => r.email },
      { key: 'cp', header: t('admin.extranetUsers.agencyUserFullName'), getValue: (r) => r.contactPersonName },
      {
        key: 'lastUpdatedAt',
        header: t('profile.lastUpdatedAt'),
        getValue: (r) => r.updatedAt,
        render: (r) => <span style={{ color: 'var(--t2)' }}>{r.updatedAt}</span>,
      },
      {
        key: 'status',
        header: t('common.status'),
        getValue: (r) => r.status,
        render: (r) => (
          <span className={`pill ${r.status === 'Active' ? 'p-com' : 'p-arc'}`}>
            {r.status === 'Active' ? t('common.active') : t('common.inactive')}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <Link
              className="act-b"
              to={`/admin/extranet-users/${encodeURIComponent(r.id)}/edit`}
              data-tip={t('admin.extranetUsers.editUser')}
              aria-label={t('admin.extranetUsers.editUser')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </Link>
            {r.status === 'Inactive' ? (
              <button
                className="act-b"
                type="button"
                disabled={deletingId === r.id}
                data-tip={t('admin.extranetUsers.deleteUser')}
                aria-label={t('admin.extranetUsers.deleteUser')}
                onClick={() => {
                  setConfirmDelete(r);
                }}
                style={{ color: 'var(--red)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M3 6h18" />
                  <path d="M8 6V4h8v2" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
              </button>
            ) : null}
          </div>
        ),
      },
    ];
  }, [deletingId, t]);

  return (
    <>
      <div className="bc">
        <span>{t('admin.userAdministration')}</span>
        <span className="bc-sep">/</span>
        {t('admin.extranetUsers.title')}
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('admin.extranetUsers.title')}</div>
        </div>
        <Link to="/admin/extranet-users/new" className="btn-create" style={{ textDecoration: 'none' }}>
          {t('admin.extranetUsers.addNewUser')}
        </Link>
      </div>

      <div className="flt-card">
        <div className="flt-grid">
          <div className="flt-f">
            <div className="flt-l">{t('admin.shared.agencyName')}</div>
            <select className={fltSelClass(qAgency)} value={qAgency} onChange={(e) => setQAgency(e.target.value)}>
              <option value="" disabled hidden>
                {t('admin.extranetUsers.selectAgencyName')}
              </option>
              {agencyOptions.map((nm) => (
                <option key={nm} value={nm}>
                  {nm}
                </option>
              ))}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('profile.emailUsername')}</div>
            <input
              className="flt-inp"
              value={qEmail}
              onChange={(e) => setQEmail(e.target.value)}
              placeholder={t('admin.shared.emailPlaceholderShort')}
            />
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('common.status')}</div>
            <select className={fltSelClass(status)} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="" disabled hidden>
                {t('admin.shared.selectStatus')}
              </option>
              <option value="Active">{t('common.active')}</option>
              <option value="Inactive">{t('common.inactive')}</option>
            </select>
          </div>
        </div>
        <div className="flt-actions">
          <button
            className="flt-btn"
            type="button"
            onClick={() => {
              setQAgency('');
              setQEmail('');
              setStatus('');
              setApplied({ agency: '', email: '', status: '' });
            }}
          >
            {t('common.reset')}
          </button>
          <button
            className="flt-btn pr"
            type="button"
            onClick={() => {
              setApplied({ agency: qAgency, email: qEmail, status });
            }}
          >
            {t('common.search')}
          </button>
        </div>
      </div>

      {error ? <div className="si-err">{error}</div> : null}

      <DataTable
        title={t('admin.extranetUsers.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        enableSorting={false}
        emphasizedHeader
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        defaultRowsPerPage={5}
        rowsPerPageOptions={[5, 10, 15]}
        footerCountNoun={t('admin.extranetUsers.footerCountNoun')}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
      />

      <ConfirmDeleteModal
        open={!!confirmDelete}
        title={t('admin.extranetUsers.deleteTitle')}
        entityLabel={t('admin.extranetUsers.entityLabel')}
        entityName={confirmDelete?.contactPersonName || confirmDelete?.email || ''}
        deleting={!!deletingId}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (!confirmDelete) return;
          const r = confirmDelete;
          void runDelete(r)
            .then(() => setConfirmDelete(null))
            .catch((e: unknown) => {
              setError(e instanceof Error ? e.message : t('admin.extranetUsers.deleteFailed'));
              navigate(location.pathname + location.search, {
                replace: true,
                state: { toast: { message: t('admin.extranetUsers.deleteFailed'), variant: 'error' } },
              });
            });
        }}
      />
    </>
  );
}
